import React, { useEffect, useState } from 'react';
import CompanyQuestion from '../../components/ToolsComponents/CompanyQuestion';
import {
    UnifiedAnswerType,
    AllAnswerOwnProps,
    IActionCustomFile,
} from '../../components/ToolsComponents/AnswerActionComponents/AnswerActionComponents.interfaces';
import { ICompanyQuestion } from '../../common';
import { useAppDispatch, useAppSelector } from '../../base';
import { StyledPageContainer } from './ToolsCompanyDetailsPage.styles';
import { toolsSelectors } from '../../actions/tools/redux/selectors';
import { toolsActions } from '../../actions';
import AllQuestionsAnswered from '../../components/ToolsComponents/AllQuestionsAnswered';
import { useAnswerComponentConfig } from '../../hooks/useAnswerComponentConfig';
import PargodaLoading from '../../components/PargodaLoading';

const ToolsCompanyDetailsPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const [questionData, setQuestionData] = useState<ICompanyQuestion | null>(
        null
    );
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const activeCompanyQuestionsCategory = useAppSelector(
        toolsSelectors.getActiveCompanyQuestionsCategory
    );
    const companyCategories = useAppSelector(
        toolsSelectors.getCompanyCategories
    );
    const activeCompany = useAppSelector(toolsSelectors.getActiveCompany);
    const editQuestionAnswer = useAppSelector(
        toolsSelectors.getActiveEditQuestionAnswer
    );

    // Call hook unconditionally
    const answerComponentConfig = useAnswerComponentConfig(questionData);

    useEffect(() => {
        if (editQuestionAnswer) {
            setQuestionData(editQuestionAnswer);
            setCurrentQuestionIndex(0);
        }

        return () => {
            dispatch(toolsActions.setActiveEditQuestionAnswer(null));
        };
    }, [editQuestionAnswer, dispatch]);

    useEffect(() => {
        if (
            !activeCompanyQuestionsCategory &&
            companyCategories.length > 0 &&
            !editQuestionAnswer
        ) {
            dispatch(
                toolsActions.setActiveCompanyQuestionsCategory(
                    companyCategories[0]
                )
            );
        }
    }, [
        activeCompanyQuestionsCategory,
        companyCategories,
        dispatch,
        editQuestionAnswer,
    ]);

    useEffect(() => {
        if (activeCompanyQuestionsCategory && !editQuestionAnswer) {
            const questions = activeCompanyQuestionsCategory.questions;
            if (questions.length > 0) {
                setQuestionData(questions[0]);
                setCurrentQuestionIndex(0);
            }
        }
    }, [activeCompanyQuestionsCategory, editQuestionAnswer]);

    if (!activeCompanyQuestionsCategory && !editQuestionAnswer) {
        return (
            <StyledPageContainer>
                <PargodaLoading height={600} width={600} />
            </StyledPageContainer>
        );
    }

    const {
        Component: AnswerComponentToRender,
        props: answerSpecificProps,
        initialAnswer,
    } = answerComponentConfig || {}; // answerComponentConfig is guaranteed to be non-null here due to the check above

    const handleAnswer = (answer: UnifiedAnswerType) => {
        let answerToSend: string = '';
        if (typeof answer === 'object' && answer && 'value' in answer) {
            answerToSend = answer.value || '';
        } else if (Array.isArray(answer)) {
            answerToSend = answer.join(', ');
        } else if (typeof answer === 'string') {
            answerToSend = answer;
        } else if (typeof answer === 'boolean') {
            answerToSend = String(answer);
        } else if (
            answer instanceof File &&
            'preview' in answer &&
            'name' in answer
        ) {
            const fileAnswer = answer as IActionCustomFile;
            console.log(
                'Answer type: File (ActionCustomFile). Name:',
                fileAnswer.name
            );
        } else {
            console.log('Answer type: Unhandled', answer);
        }
        if (answerToSend !== '') {
            dispatch(
                toolsActions.postCompanyQuestionAnswer({
                    companyId: activeCompany?.value || '',
                    questionId: questionData?.id.toString() || '',
                    answer: answerToSend,
                })
            );
        } else {
            console.log('Answer is empty, skipping');
        }
    };

    const handleSkip = () => {
        if (
            activeCompanyQuestionsCategory &&
            currentQuestionIndex <
                activeCompanyQuestionsCategory.questions.length - 1
        ) {
            const questions = activeCompanyQuestionsCategory.questions;
            setQuestionData(questions[currentQuestionIndex + 1]);
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else if (companyCategories.length > 0) {
            const unfinishedCategory = companyCategories.find(
                (category) =>
                    category.percentage < 100 &&
                    category.id !== activeCompanyQuestionsCategory?.id
            );
            if (unfinishedCategory) {
                dispatch(
                    toolsActions.setActiveCompanyQuestionsCategory(
                        unfinishedCategory
                    )
                );
            }
        } else {
            dispatch(toolsActions.setActiveCompanyQuestionsCategory(null));
        }
    };

    return (
        <StyledPageContainer>
            {(activeCompanyQuestionsCategory?.questions?.length &&
                activeCompanyQuestionsCategory?.questions?.length > 0 &&
                AnswerComponentToRender) ||
            questionData ? (
                <CompanyQuestion<UnifiedAnswerType, AllAnswerOwnProps>
                    question={questionData?.title || ''} // questionData is guaranteed non-null here
                    subtitle={questionData?.subtitle || ''}
                    AnswerComponent={
                        AnswerComponentToRender as React.ComponentType<
                            AllAnswerOwnProps & {
                                value: UnifiedAnswerType | undefined;
                                onSelectionChange: (
                                    value: UnifiedAnswerType
                                ) => void;
                            }
                        >
                    }
                    answerProps={answerSpecificProps}
                    initialAnswer={initialAnswer}
                    onAnswer={handleAnswer}
                    onSkip={handleSkip}
                />
            ) : (
                <AllQuestionsAnswered />
            )}
        </StyledPageContainer>
    );
};

export default ToolsCompanyDetailsPage;
